<template>
    <div class="bg-surface-0 dark:bg-surface-900 flex flex-col h-full">
        <div class="relative flex items-center justify-between 2xl:gap-6" style="min-height: 65px;">
            <div class="flex items-center gap-3 pl-8 py-4 cursor-pointer" @click="goHome" data-cy="homeButton">
                <StaticImage filename="datalane.svg" alt="Image" height="30" width="30"/>
            </div>
            <a
                v-styleclass="{
                    selector: '@next',
                    enterFromClass: 'hidden',
                    leaveToClass: 'hidden',
                    hideOnOutsideClick: true
                }"
                class="mr-8 cursor-pointer block md:hidden text-surface-900 dark:text-surface-0"
            >
                <i class="pi pi-bars text-xl" />
            </a>
            <div
                class="flex-1 md:shadow-none shadow-md rounded-b-xl md:rounded-none pr-4 top-full right-0 w-full bg-surface-0 dark:bg-surface-900 absolute md:static md:flex hidden md:flex-row flex-col items-center justify-between transition-all"
            >
                <div class="flex items-center gap-2 md:flex-row flex-col w-full">
                    <slot name="navbar"></slot>
                </div>
                <div class="flex items-center">
                    <div class="flex md:flex-row flex-col gap-2 md:items-center md:mt-0 mt-4 w-full">
                        <div class="flex items-center xl:pr-8">
                            <Button icon="pi pi-question-circle" variant="text" rounded aria-label="Help" @click="toggleHelpMenu"/>
                            <Menu ref="helpMenu" id="help_menu" :model="helpMenuItems" :popup="true" />
                        </div>
                        <Divider layout="vertical" class="!hidden md:!block !mx-1" />
                        <div class="flex items-center xl:pr-8">
                            <Button icon="pi pi-user" variant="text" rounded aria-label="User" @click="toggleUserMenu"/>
                            <Menu ref="userMenu" id="user_menu" :model="userMenuItems" :popup="true" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="p-6 bg-surface-50 dark:bg-surface-950 h-full overflow-hidden relative">
            <slot></slot>
        </div>
        <Toast />
        <Toast position="top-left" group="tl" />
        <Toast position="bottom-left" group="bl" />
        <Toast position="bottom-right" group="br" />
        <Toast position="bottom-center" group="bc" />
    </div>
</template>
<script setup>
import {computed, onMounted, ref, watch} from "vue";
import { useRouter } from 'vue-router'
import { useStore } from "vuex";
import {ROUTE_NAMES} from "@/src/router";
const router = useRouter()
const store = useStore()

import { useToast } from 'primevue/usetoast';
import { getDocsLink } from '@/src/utils/utils';
import {useLastApiError} from "@/src/queries/errors";

const toast = useToast();

const userMenu = ref()

const userEmail = computed(() => store.state.userStore.user.email)

const toggleUserMenu = (event) => {
    userMenu.value.toggle(event)
}

const helpMenu = ref()
const helpMenuItems = computed(() => {
    return [
        {
            label: 'Read the docs',
            icon: 'pi pi-book',
            command: () => {
                window.open(docsLink.value, '_blank')
            }
        },
        {
            label: 'Get support',
            icon: 'pi pi-headphones',
            command: () => {
                window.open('https://datalane.atlassian.net/servicedesk/customer/portal/1/group/2', '_blank')
            }
        }
    ]
})

const toggleHelpMenu = (event) => {
    helpMenu.value.toggle(event)
}

const globalPermissions = computed(() => store.getters['userStore/globalPermissions'])

const userMenuItems = computed(() => {
    return [
        {
            label: userEmail.value,
            icon: 'pi pi-user',
            disabled: true,
            visible: true
        },
        {
            label: 'Preferences',
            icon: 'pi pi-cog',
            command: () => {
                router.push({ name: ROUTE_NAMES.PREFERENCES })
            },
            visible: true
        },
        {
            label: 'User Management',
            icon: 'pi pi-users',
            command: () => {
                router.push({ name: ROUTE_NAMES.USER_MANAGEMENT })
            },
            visible: globalPermissions.value.hasOwnProperty('manage_users')
        },
        {
            label: 'System settings',
            icon: 'pi pi-wrench',
            command: () => {
                router.push({ name: ROUTE_NAMES.SETTINGS })
            },
            visible: globalPermissions.value.hasOwnProperty('manage_settings')
        },
        {
            label: 'Sign out',
            icon: 'pi pi-sign-out',
            command: () => {
                store.dispatch('userStore/logout')
            },
            visible: true
        }
    ].filter(item => item.visible)
})


onMounted(async () => {
    await store.dispatch('userStore/loadGlobalPermissions')
})

const goHome = async() => {
    store.dispatch('repoStore/setSelectedSpace', null)
    store.dispatch('repoStore/setSelectedGraph', null)
    await store.dispatch('repoStore/loadWorkspaces')
}

const error = useLastApiError()


const docsLink = computed(() => getDocsLink('index.html'))

watch(error, (val) => {
    if (!val) {
        return
    }
    if (val.code === "ERR_NETWORK") {
        router.replace({ name: ROUTE_NAMES.OFFLINE })
    }
    //500 error are unexpected
    if (val.status >= 500) {
        // the exception itself is only logged on the backend, the trace id lets us find it there
        const msg = [val.message || 'Something went wrong', val.traceId ? `(trace id: ${val.traceId})` : null]
            .filter(Boolean).join(' ')

        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: msg, life: 5000
        });
    }
    if (val.status === 403) {
        const msg = val.message || 'Generic Permission Error'
        //redirect to home
        toast.add({
              severity: 'error',
              summary: 'Permission Error',
              detail: msg, life: 5000
          });
        router.replace({ name: ROUTE_NAMES.HOME })
    }
    if (val.status === 401) {
        store.dispatch('userStore/logout')
    }
})

</script>